import React, { useState } from 'react';
import { useOps } from '../context/OpsContext';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Mail, Phone, Plus, Filter, ChevronRight, User, MapPin, CreditCard, BookOpen, Upload, FileDown } from 'lucide-react';
import ImportModal from '../components/ImportModal';
import ExportButton from '../components/ExportButton';
import './Customers.css';

const Customers = () => {
    const { customers = [], selectedBranch, handleTemplateImport, packages = [], branches = [], loadMoreCustomers, hasMoreCustomers, totalCustomers } = useOps();
    const [searchTerm, setSearchTerm] = useState('');
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const navigate = useNavigate();

    const filtered = (customers || []).filter(c => {
        const matchesSearch = (c.name || `${c.first_name || ''} ${c.last_name || ''}`).toLowerCase().includes(searchTerm.toLowerCase()) ||
            (c.phone || c.phone_number || '').includes(searchTerm);
        const matchesBranch = selectedBranch === 'All Branches' ? true : (c.branch === selectedBranch || c.branchName === selectedBranch);
        return matchesSearch && matchesBranch;
    });

    const getPackageName = (id) => {
        const pkg = packages.find(p => String(p.id) === String(id));
        return pkg ? pkg.name : 'Standard Training';
    };

    const exportColumns = [
        { header: 'Full Name', accessor: c => c.name || `${c.first_name || ''} ${c.last_name || ''}` },
        { header: 'Mobile', accessor: c => c.phone || c.phone_number },
        { header: 'Email', accessor: c => c.email || c.email_address },
        { header: 'ID Number', accessor: c => c.id_number || c.national_id },
        { header: 'Package', accessor: c => getPackageName(c.packageId || c.package_id) },
        { header: 'Status', accessor: c => c.status },
        { header: 'Balance', accessor: c => c.balance },
    ];

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Student Portfolio</h2>
                    <p className="page-subtitle">View and manage your active student directory</p>
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn btn-secondary flex items-center gap-2"
                        onClick={() => setIsImportModalOpen(true)}
                    >
                        <Upload size={18} /> Bulk Import
                    </button>
                    <ExportButton
                        data={filtered}
                        columns={exportColumns}
                        fileName="customers_export"
                        className="btn btn-primary flex items-center gap-2"
                    />
                </div>
            </div>

            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by student name or contact details..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-secondary flex items-center gap-2">
                    <Filter size={16} /> Filter
                </button>
            </div>

            <div className="customers-grid fade-in">
                {filtered.length === 0 ? (
                    <div className="col-span-full py-20 text-center card">
                        <User size={48} className="mx-auto text-slate-200 mb-4" />
                        <p className="text-secondary">No students found matching your criteria.</p>
                    </div>
                ) : (
                    filtered.map(customer => {
                        const customerName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown';
                        return (
                            <div key={customer.id} className="card customer-card" onClick={() => navigate(`/customers/${customer.id}`)} style={{ cursor: 'pointer' }}>
                                <div className="customer-header">
                                    <div className="customer-avatar">
                                        {customerName.charAt(0)}
                                    </div>
                                    <div className="customer-info">
                                        <h4 className="customer-name">{customerName}</h4>
                                        <span className="customer-id">{customer.student_id_number || customer.national_id || customer.id}</span>
                                    </div>
                                    <div className="icon-btn"><ChevronRight size={20} /></div>
                                </div>
                                <div className="customer-details">
                                    <div className="detail-item">
                                        <span className="label">Program</span>
                                        <span className="value">{getPackageName(customer.packageId || customer.package_id)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Stage</span>
                                        <span className="value text-gold">{customer.stage || 'Theory'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Branch</span>
                                        <span className="value">{customer.branch || 'Main'}</span>
                                    </div>
                                    <div className="detail-item">
                                        <span className="label">Balance</span>
                                        <span className={`value ${parseFloat(customer.balance || 0) > 0 ? 'text-red' : 'text-green'}`}>
                                            ${parseFloat(customer.balance || 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {hasMoreCustomers && (
                <div className="flex justify-center mt-6">
                    <button className="btn btn-secondary" onClick={loadMoreCustomers}>
                        Load More ({customers.length} of {totalCustomers})
                    </button>
                </div>
            )}

            <ImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImport={handleTemplateImport}
                title="Import Customers"
                templateHeaders={['First Name', 'Last Name', 'Phone', 'Email', 'National ID', 'Gender', 'Package', 'Branch']}
                sampleRow={['John', 'Doe', '0771234567', 'john@example.com', '63-123456-X-63', 'Male', 'Full Course', 'Harare']}
            />
        </div>
    );
};

export default Customers;
